import { z } from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { practiceIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { serviceValuesSchema } from './service-catalog.models';

export const SERVICE_CATALOG_ADD_SERVICE_TYPE = 'service-catalog/add-service';

export const serviceCatalogAddService = defineCommand(
    SERVICE_CATALOG_ADD_SERVICE_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.recordId),
        ...serviceValuesSchema.shape,
        makeDefault: z.boolean().meta(metadata.nonSensitive),
    }),
    z.void(),
);

export const SERVICE_CATALOG_UPDATE_SERVICE_TYPE = 'service-catalog/update-service';

// `code` names the service and is never written — the command replaces the other three values
// only. Rewriting which row `code` addresses is an application-layer concern, not a schema one.
export const serviceCatalogUpdateService = defineCommand(
    SERVICE_CATALOG_UPDATE_SERVICE_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.recordId),
        ...serviceValuesSchema.shape,
        makeDefault: z.boolean().meta(metadata.nonSensitive),
    }),
    z.void(),
);

export const SERVICE_CATALOG_DELETE_SERVICE_TYPE = 'service-catalog/delete-service';

export const serviceCatalogDeleteService = defineCommand(
    SERVICE_CATALOG_DELETE_SERVICE_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.recordId),
        code: serviceValuesSchema.shape.code,
    }),
    z.void(),
);

export const SERVICE_CATALOG_SET_DEFAULT_SERVICE_TYPE = 'service-catalog/set-default-service';

export const serviceCatalogSetDefaultService = defineCommand(
    SERVICE_CATALOG_SET_DEFAULT_SERVICE_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.recordId),
        code: serviceValuesSchema.shape.code,
    }),
    z.void(),
);

export const SERVICE_CATALOG_UPDATE_RATES_TYPE = 'service-catalog/update-rates';

const serviceRateSchema = z.object({
    code: serviceValuesSchema.shape.code,
    rateCents: serviceValuesSchema.shape.rateCents,
});

export const serviceCatalogUpdateRates = defineCommand(
    SERVICE_CATALOG_UPDATE_RATES_TYPE,
    z
        .object({
            practiceId: practiceIdSchema.meta(metadata.recordId),
            rates: z.array(serviceRateSchema).min(1),
        })
        .superRefine((value, ctx) => {
            const seen = new Set<string>();
            value.rates.forEach((rate, index) => {
                if (seen.has(rate.code)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Each service code can appear only once',
                        path: ['rates', index, 'code'],
                    });
                }
                seen.add(rate.code);
            });
        }),
    z.void(),
);
